import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { toast } from "sonner";

const statuses = {
  new: "New",
  discussing: "Discussing",
  waiting_details: "Waiting for details",
  quote_sent: "Order link / quotation sent",
  won: "Won",
  lost: "Lost",
  on_hold: "On hold",
};
const channels = [
  "facebook",
  "instagram",
  "tiktok",
  "whatsapp",
  "website",
  "referral",
  "other",
];
const reasons = [
  "unknown",
  "price",
  "size_unavailable",
  "product_unavailable",
  "delivery",
  "fit",
  "postponed",
  "no_response",
  "other",
];
const languages = ["English", "Sinhala", "Mixed Sinhala/English"];
const blank = {
  name: "",
  channel: "facebook",
  channel_handle: "",
  phone: "",
  email: "",
  interests: [],
  conversation: "",
  notes: "",
  status: "new",
  next_action: "",
  next_action_at: null,
  assigned_user_id: null,
  lost_reason: "unknown",
  lost_evidence: "",
  customer_id: null,
  order_ids: [],
  marketing_permission: "unknown",
  permission_source: "",
  version: 0,
};
const errorText = (e) =>
  typeof e.response?.data?.detail === "string"
    ? e.response.data.detail
    : "Could not complete the request. Check required fields and try again.";
const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white p-2 text-sm";
const buttonClass =
  "rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40";
function Field({ label, children }) {
  return (
    <label className="block space-y-1 text-sm font-medium text-slate-700">
      <span>{label}</span>
      {children}
    </label>
  );
}
function Select({ value, onChange, options }) {
  return (
    <select
      className={inputClass}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>
          {o.label ?? o}
        </option>
      ))}
    </select>
  );
}
function Panel({ children }) {
  return (
    <div className="rounded-xl border bg-white p-5 shadow-sm">{children}</div>
  );
}
export default function Growth() {
  const { user } = useAuth();
  const { section = "dashboard" } = useParams();
  if (!["admin", "manager"].includes(user?.role))
    return <Panel>Administrator or manager access is required.</Panel>;
  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4">
      <div>
        <h1 className="text-2xl font-bold">AI Sales & Growth</h1>
        <p className="text-sm text-slate-500">
          Manage inquiries, follow-ups and reviewed reply drafts.
        </p>
      </div>
      {section === "leads" ? (
        <Leads />
      ) : section === "settings" ? (
        <Settings admin={user.role === "admin"} />
      ) : (
        <Dashboard />
      )}
    </div>
  );
}
function Dashboard() {
  const [dates, setDates] = useState({
    from: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10),
  });
  const [data, setData] = useState(null),
    [names, setNames] = useState({}),
    [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [r, l] = await Promise.all([
        api.get("/growth/dashboard", {
          params: { date_from: dates.from, date_to: dates.to },
        }),
        api.get("/growth/lookups"),
      ]);
      setData(r.data);
      setNames(Object.fromEntries(l.data.products.map((p) => [p.id, p.name])));
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  }, [dates]);
  useEffect(() => {
    load();
  }, [load]); // initial range; subsequent changes require Apply
  return (
    <>
      <Panel>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Inquiry date from (UTC)">
            <input
              className={inputClass}
              type="date"
              value={dates.from}
              onChange={(e) => setDates({ ...dates, from: e.target.value })}
            />
          </Field>
          <Field label="Through (inclusive)">
            <input
              className={inputClass}
              type="date"
              value={dates.to}
              onChange={(e) => setDates({ ...dates, to: e.target.value })}
            />
          </Field>
          <button className={buttonClass} disabled={busy} onClick={load}>
            {busy ? "Loading…" : "Apply"}
          </button>
        </div>
      </Panel>
      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Recorded inquiries", data.sample_size],
              ["Converted leads", data.converted_leads],
              [
                "Conversion",
                data.conversion_percent === null
                  ? "—"
                  : `${data.conversion_percent}%`,
              ],
              ["Overdue actions (all open leads)", data.overdue_actions],
              ["Linked orders", data.linked_orders],
              ["Delivered / collected", data.fulfilled_orders],
              ["Returned orders", data.returned_orders],
              ["Orders with refunds", data.refunded_orders],
            ].map(([k, v]) => (
              <Panel key={k}>
                <p className="text-sm text-slate-500">{k}</p>
                <strong className="text-3xl">{v}</strong>
              </Panel>
            ))}
          </div>
          <Panel>
            <p className="text-sm">{data.definition}</p>
            <p className="mt-2 text-xs text-slate-500">
              Sample: {data.sample_size} leads • {data.date_from}–{data.date_to}{" "}
              • Outcomes as of {new Date(data.outcome_cutoff).toLocaleString()}.
              Small samples indicate tentative patterns.
            </p>
            <p className="mt-2 text-xs">
              Qualifying orders: confirmed, processing, shipped, completed or
              delivered; excluding failed payments, cancellations and full
              returns/refunds. Each converted lead counts once.
            </p>
          </Panel>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              ["Inquiries by source", data.sources],
              ["Recorded lost-sale reasons", data.lost_reasons],
            ].map(([title, values]) => (
              <Panel key={title}>
                <h2 className="mb-3 font-bold">{title}</h2>
                {Object.entries(values).length ? (
                  Object.entries(values).map(([k, v]) => (
                    <p key={k} className="flex justify-between border-b py-2">
                      <span>{k.replaceAll("_", " ")}</span>
                      <strong>{v}</strong>
                    </p>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">
                    No records for this period.
                  </p>
                )}
              </Panel>
            ))}
          </div>
          <Panel>
            <h2 className="font-bold">Recorded product / size demand</h2>
            {data.demand.length ? (
              data.demand.map((d, i) => (
                <p key={i} className="border-b py-2 text-sm">
                  {names[d.product_id] || d.product_id} •{" "}
                  {d.size || "Size unknown"} • {d.fit || "Fit unknown"} —{" "}
                  {d.leads} lead(s)
                </p>
              ))
            ) : (
              <p className="text-sm">No product requests recorded.</p>
            )}
          </Panel>
        </>
      )}
    </>
  );
}
function Leads() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState({ items: [], total: 0 }),
    [look, setLook] = useState({
      products: [],
      customers: [],
      users: [],
      sales_orders: [],
    });
  const [filters, setFilters] = useState({
      search: "",
      status: "",
      channel: "",
      overdue: false,
      owner: "",
    }),
    [offset, setOffset] = useState(0);
  const [lead, setLead] = useState(null),
    [activities, setActivities] = useState([]),
    [drafts, setDrafts] = useState([]),
    [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false),
    [job, setJob] = useState(null),
    [note, setNote] = useState(""),
    [ai, setAI] = useState({
      language: "Mixed Sinhala/English",
      tone: "Friendly and concise",
      length: "short",
    });
  const [aiEnabled, setAIEnabled] = useState(false);
  const load = useCallback(async () => {
    try {
      const p = {
        offset,
        limit: 50,
        ...Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== "" && v !== false),
        ),
      };
      const r = await api.get("/growth/leads", { params: p });
      setRows(r.data);
    } catch (e) {
      toast.error(errorText(e));
    }
  }, [offset, filters]);
  const open = useCallback(async (id) => {
    try {
      const r = await api.get(`/growth/leads/${id}`);
      setLead(r.data.lead);
      setActivities(r.data.activities);
      setDrafts(r.data.drafts);
      setMatches([]);
    } catch (e) {
      toast.error(errorText(e));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    api
      .get("/growth/lookups")
      .then((r) => setLook(r.data))
      .catch((e) => toast.error(errorText(e)));
    api
      .get("/growth/settings")
      .then((r) => {
        setAIEnabled(r.data.enabled && r.data.provider_ready);
        setAI((a) => ({ ...a, language: r.data.language, tone: r.data.tone }));
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    const id = params.get("lead");
    if (id) open(id);
  }, [params, open]);
  const jobId = job?.id,
    jobStatus = job?.status,
    currentLeadId = lead?.id;
  useEffect(() => {
    if (!jobId || !["queued", "running"].includes(jobStatus)) return;
    let active = true;
    const timer = setInterval(async () => {
      try {
        const r = await api.get(`/growth/jobs/${jobId}`);
        if (!active) return;
        setJob(r.data);
        if (r.data.status === "succeeded") {
          toast.success("Draft ready for review");
          if (currentLeadId === r.data.lead_id) open(r.data.lead_id);
        }
        if (r.data.status === "failed") toast.error(r.data.failure_reason);
      } catch (e) {
        if (active) {
          setJob(null);
          toast.error(errorText(e));
        }
      }
    }, 3000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [jobId, jobStatus, currentLeadId, open]);
  const change = (key, value) => setLead((l) => ({ ...l, [key]: value }));
  const save = async (allowDuplicate) => {
    setBusy(true);
    try {
      if (!allowDuplicate) {
        const d = await api.post("/growth/duplicates", lead, {
          params: { exclude_id: lead.id },
        });
        if (d.data.length) {
          setMatches(d.data);
          return;
        }
      }
      const r = lead.id
        ? await api.put(`/growth/leads/${lead.id}`, lead)
        : await api.post("/growth/leads", lead);
      setLead(r.data);
      setParams({ lead: r.data.id });
      await open(r.data.id);
      await load();
      toast.success("Lead saved");
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const request = async (action) => {
    try {
      const r = await api.post(`/growth/leads/${lead.id}/ai`, {
        ...ai,
        action,
      });
      setJob(r.data);
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  const options = (list, label) => [
    { value: "", label: "Not selected" },
    ...list.map((x) => ({ value: x.id, label: x[label] || x.id })),
  ];
  return (
    <>
      <Panel>
        <div className="flex flex-wrap gap-2">
          <input
            className="rounded-lg border p-2 text-sm"
            placeholder="Name, handle, phone or email"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <button
            className={buttonClass}
            onClick={() => {
              setOffset(0);
              load();
            }}
          >
            Search
          </button>
          <Select
            value={filters.status}
            onChange={(v) => {
              setOffset(0);
              setFilters({ ...filters, status: v });
            }}
            options={[
              { value: "", label: "All statuses" },
              ...Object.entries(statuses).map(([value, label]) => ({
                value,
                label,
              })),
            ]}
          />
          <Select
            value={filters.channel}
            onChange={(v) => {
              setOffset(0);
              setFilters({ ...filters, channel: v });
            }}
            options={[{ value: "", label: "All channels" }, ...channels]}
          />
          <Select
            value={filters.owner}
            onChange={(v) => {
              setOffset(0);
              setFilters({ ...filters, owner: v });
            }}
            options={[
              { value: "", label: "All owners" },
              ...look.users.map((u) => ({ value: u.id, label: u.full_name })),
            ]}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={filters.overdue}
              onChange={(e) => {
                setOffset(0);
                setFilters({ ...filters, overdue: e.target.checked });
              }}
            />
            Overdue only
          </label>
          <button
            className={buttonClass}
            onClick={() => {
              setParams({});
              setLead({ ...blank });
              setDrafts([]);
              setActivities([]);
              setMatches([]);
            }}
          >
            New lead
          </button>
        </div>
      </Panel>
      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <Panel>
          <p className="mb-3 text-sm text-slate-500">{rows.total} lead(s)</p>
          {rows.items.map((l) => (
            <button
              key={l.id}
              className={`mb-2 w-full rounded-lg border p-3 text-left ${lead?.id === l.id ? "border-slate-900 bg-slate-50" : ""}`}
              onClick={() => setParams({ lead: l.id })}
            >
              <strong className="block">{l.name}</strong>
              <span className="text-xs">
                {l.channel} • {statuses[l.status]}
              </span>
              <p className="text-xs text-slate-500">
                {l.next_action || "No next action"}{" "}
                {l.next_action_at &&
                  `• ${new Date(l.next_action_at).toLocaleString()}`}
              </p>
            </button>
          ))}
          <div className="flex justify-between">
            <button
              disabled={!offset}
              onClick={() => setOffset(Math.max(0, offset - 50))}
            >
              Previous
            </button>
            <button
              disabled={offset + 50 >= rows.total}
              onClick={() => setOffset(offset + 50)}
            >
              Next
            </button>
          </div>
        </Panel>
        {lead ? (
          <div className="space-y-4">
            <Panel>
              <h2 className="mb-4 text-lg font-bold">
                {lead.id ? "Lead details" : "New inquiry"}
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  ["Name / display name", "name"],
                  ["Channel handle", "channel_handle"],
                  ["Phone (provided by customer)", "phone"],
                  ["Email", "email"],
                ].map(([label, k]) => (
                  <Field key={k} label={label}>
                    <input
                      className={inputClass}
                      value={lead[k]}
                      onChange={(e) => change(k, e.target.value)}
                    />
                  </Field>
                ))}
                <Field label="Source">
                  <Select
                    value={lead.channel}
                    onChange={(v) => change("channel", v)}
                    options={channels}
                  />
                </Field>
                <Field label="Status">
                  <Select
                    value={lead.status}
                    onChange={(v) => change("status", v)}
                    options={Object.entries(statuses).map(([value, label]) => ({
                      value,
                      label,
                    }))}
                  />
                </Field>
                <Field label="Assigned staff">
                  <Select
                    value={lead.assigned_user_id}
                    onChange={(v) => change("assigned_user_id", v || null)}
                    options={options(look.users, "full_name")}
                  />
                </Field>
                <Field label="Confirmed customer link">
                  <Select
                    value={lead.customer_id}
                    onChange={(v) => change("customer_id", v || null)}
                    options={options(look.customers, "name")}
                  />
                </Field>
                <Field label="Next action">
                  <input
                    className={inputClass}
                    value={lead.next_action}
                    onChange={(e) => change("next_action", e.target.value)}
                  />
                </Field>
                <Field label="Next action date / time (your timezone)">
                  <input
                    type="datetime-local"
                    className={inputClass}
                    value={
                      lead.next_action_at
                        ? new Date(
                            new Date(lead.next_action_at).getTime() -
                              new Date(
                                lead.next_action_at,
                              ).getTimezoneOffset() *
                                60000,
                          )
                            .toISOString()
                            .slice(0, 16)
                        : ""
                    }
                    onChange={(e) =>
                      change(
                        "next_action_at",
                        e.target.value
                          ? new Date(e.target.value).toISOString()
                          : null,
                      )
                    }
                  />
                </Field>
              </div>
              <div className="mt-4 space-y-3">
                <Field label="Conversation — paste anonymised text where possible">
                  <textarea
                    rows={6}
                    className={inputClass}
                    value={lead.conversation}
                    onChange={(e) => change("conversation", e.target.value)}
                  />
                </Field>
                <Field label="Staff notes">
                  <textarea
                    className={inputClass}
                    value={lead.notes}
                    onChange={(e) => change("notes", e.target.value)}
                  />
                </Field>
                <Field label="Lost-sale reason (customer evidence where available)">
                  <Select
                    value={lead.lost_reason}
                    onChange={(v) => change("lost_reason", v)}
                    options={reasons}
                  />
                </Field>
                <Field label="Evidence / customer statement">
                  <textarea
                    className={inputClass}
                    value={lead.lost_evidence}
                    onChange={(e) => change("lost_evidence", e.target.value)}
                  />
                </Field>
                <p className="text-xs text-slate-500">
                  No response does not establish a price objection.
                </p>
                <Field label="Marketing permission (separate from inquiry)">
                  <Select
                    value={lead.marketing_permission}
                    onChange={(v) => change("marketing_permission", v)}
                    options={["unknown", "granted", "declined", "withdrawn"]}
                  />
                </Field>
                <Field label="Permission source / evidence">
                  <input
                    className={inputClass}
                    value={lead.permission_source}
                    onChange={(e) =>
                      change("permission_source", e.target.value)
                    }
                  />
                </Field>
              </div>
              <h3 className="my-3 font-semibold">Requested products</h3>
              {lead.interests.map((i, index) => (
                <div className="mb-3 grid gap-2 sm:grid-cols-2" key={index}>
                  <Select
                    value={i.product_id}
                    options={options(look.products, "name")}
                    onChange={(v) =>
                      change(
                        "interests",
                        lead.interests.map((x, n) =>
                          n === index
                            ? { ...x, product_id: v, variation_id: null }
                            : x,
                        ),
                      )
                    }
                  />
                  {["size", "fit", "quantity"].map((k) => (
                    <Field label={k} key={k}>
                      <input
                        className={inputClass}
                        type={k === "quantity" ? "number" : "text"}
                        min={1}
                        value={i[k]}
                        onChange={(e) =>
                          change(
                            "interests",
                            lead.interests.map((x, n) =>
                              n === index
                                ? {
                                    ...x,
                                    [k]:
                                      k === "quantity"
                                        ? Number(e.target.value)
                                        : e.target.value,
                                  }
                                : x,
                            ),
                          )
                        }
                      />
                    </Field>
                  ))}
                  <button
                    className="text-sm text-red-600"
                    onClick={() =>
                      change(
                        "interests",
                        lead.interests.filter((_, n) => n !== index),
                      )
                    }
                  >
                    Remove product
                  </button>
                </div>
              ))}
              <button
                className="text-sm underline"
                onClick={() =>
                  change("interests", [
                    ...lead.interests,
                    {
                      product_id: "",
                      size: "",
                      fit: "",
                      quantity: 1,
                      variation_id: null,
                    },
                  ])
                }
              >
                Add requested product
              </button>
              <h3 className="my-3 font-semibold">Confirmed order links</h3>
              <Select
                value=""
                options={options(
                  look.sales_orders.filter(
                    (o) => !lead.order_ids.includes(o.id),
                  ),
                  "order_number",
                )}
                onChange={(v) =>
                  v && change("order_ids", [...lead.order_ids, v])
                }
              />
              {lead.order_ids.map((id) => (
                <div className="flex justify-between py-2 text-sm" key={id}>
                  <a href="/sales-orders">
                    {look.sales_orders.find((o) => o.id === id)?.order_number ||
                      id}
                  </a>
                  <button
                    onClick={() =>
                      change(
                        "order_ids",
                        lead.order_ids.filter((x) => x !== id),
                      )
                    }
                  >
                    Unlink
                  </button>
                </div>
              ))}
              {matches.length > 0 && (
                <div className="my-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm">
                  <strong>Possible duplicates — review before saving</strong>
                  {matches.map((m) => (
                    <p key={m.id}>
                      {m.name} ({m.channel}, {statuses[m.status]}){" "}
                      <button
                        className="underline"
                        onClick={() => setParams({ lead: m.id })}
                      >
                        Open
                      </button>
                    </p>
                  ))}
                  <button
                    className="mt-2 underline"
                    disabled={busy}
                    onClick={() => save(true)}
                  >
                    Confirmed different person — save separately
                  </button>
                </div>
              )}
              <button
                className={`${buttonClass} mt-4`}
                disabled={busy}
                onClick={() => save(false)}
              >
                {busy ? "Saving…" : "Save lead"}
              </button>
              <p className="mt-2 text-xs text-slate-500">
                Order links do not create orders or record payments.
              </p>
            </Panel>
            {lead.id && (
              <>
                <Panel>
                  <h2 className="font-bold">AI Reply Assistant</h2>
                  <p className="my-2 text-sm text-slate-500">
                    Save changes first. Review current prices and stock before
                    manually sending a draft.
                  </p>
                  {!aiEnabled && (
                    <p className="mb-3 rounded bg-amber-50 p-3 text-sm">
                      AI is disabled until a verified provider is configured.
                      You can continue managing leads.
                    </p>
                  )}
                  <div className="grid gap-2 sm:grid-cols-3">
                    <Select
                      value={ai.language}
                      onChange={(v) => setAI({ ...ai, language: v })}
                      options={languages}
                    />
                    <input
                      aria-label="Reply tone"
                      className={inputClass}
                      value={ai.tone}
                      onChange={(e) => setAI({ ...ai, tone: e.target.value })}
                    />
                    <Select
                      value={ai.length}
                      onChange={(v) => setAI({ ...ai, length: v })}
                      options={["short", "medium", "detailed"]}
                    />
                  </div>
                  <div className="my-3 flex flex-wrap gap-2">
                    {[
                      ["summary", "Summarise"],
                      ["questions", "Unanswered questions"],
                      ["reply", "Suggest reply"],
                      ["next_action", "Next action"],
                      ["followup", "Follow-up"],
                    ].map(([a, t]) => (
                      <button
                        key={a}
                        className={buttonClass}
                        disabled={
                          !aiEnabled ||
                          ["queued", "running"].includes(job?.status)
                        }
                        onClick={() => request(a)}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  {job && (
                    <p className="text-sm">
                      AI job: {job.status} {job.failure_reason || ""}
                    </p>
                  )}
                  {drafts.map((d) => (
                    <Draft
                      key={d.id}
                      draft={d}
                      currentVersion={lead.version}
                      reload={() => open(lead.id)}
                    />
                  ))}
                </Panel>
                <Panel>
                  <h2 className="mb-3 font-bold">Activity history</h2>
                  <textarea
                    aria-label="Activity note"
                    className={inputClass}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <button
                    className={`${buttonClass} my-2`}
                    disabled={!note.trim()}
                    onClick={async () => {
                      try {
                        await api.post(`/growth/leads/${lead.id}/activities`, {
                          text: note,
                        });
                        setNote("");
                        open(lead.id);
                      } catch (e) {
                        toast.error(errorText(e));
                      }
                    }}
                  >
                    Add note
                  </button>
                  {activities.map((a) => (
                    <div key={a.id} className="border-t py-2 text-xs">
                      <strong>{a.kind.replaceAll("_", " ")}</strong> •{" "}
                      {new Date(a.created_at).toLocaleString()}
                      <p className="break-words whitespace-pre-wrap">
                        {typeof a.detail === "string"
                          ? a.detail
                          : JSON.stringify(a.detail, null, 2)}
                      </p>
                    </div>
                  ))}
                </Panel>
              </>
            )}
          </div>
        ) : (
          <Panel>Select a lead, or create a new inquiry.</Panel>
        )}
      </div>
    </>
  );
}
function Draft({ draft, currentVersion, reload }) {
  const [text, setText] = useState(draft.text),
    [status, setStatus] = useState(draft.status),
    [busy, setBusy] = useState(false);
  return (
    <div className="my-4 rounded-lg border p-3">
      <p className="mb-2 text-xs">
        {draft.action} • {new Date(draft.created_at).toLocaleString()} •
        Requires staff review
      </p>
      {draft.lead_version !== currentVersion && (
        <p className="mb-2 text-xs text-amber-700">
          Lead changed since generation. Verify this draft before using it.
        </p>
      )}
      <textarea
        rows={7}
        className={inputClass}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <Select
          value={status}
          onChange={setStatus}
          options={["draft", "reviewed", "sent_manually", "archived"]}
        />
        <button
          className={buttonClass}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await api.put(`/growth/drafts/${draft.id}`, { text, status });
              toast.success("Draft saved");
              reload();
            } catch (e) {
              toast.error(errorText(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          Save review
        </button>
        <button
          className="text-sm underline"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(text);
              toast.success("Copied. Send manually after review.");
            } catch {
              toast.error("Clipboard unavailable. Select and copy the text.");
            }
          }}
        >
          Copy draft
        </button>
      </div>
      <p className="mt-2 text-xs text-slate-500">
        “Sent manually” is a staff record; this module does not send messages.
      </p>
    </div>
  );
}
function Settings({ admin }) {
  const [data, setData] = useState(null),
    [usage, setUsage] = useState([]),
    [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([
        api.get("/growth/settings"),
        api.get("/growth/usage"),
      ]);
      setData(s.data);
      setUsage(u.data);
    } catch (e) {
      toast.error(errorText(e));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  if (!data) return <Panel>Loading settings…</Panel>;
  const change = (k, v) => setData({ ...data, [k]: v });
  return (
    <>
      <Panel>
        <h2 className="mb-3 font-bold">AI provider & approved knowledge</h2>
        <p className="mb-3 text-sm">
          Provider configuration:{" "}
          {data.provider_ready
            ? "Verified configuration present"
            : "Not configured / not verified"}
          . Credentials stay on the backend.
        </p>
        <p className="mb-3 text-xs">
          {data.credential_location}. Endpoint:{" "}
          {data.endpoint || "Not configured"}
        </p>
        <fieldset disabled={!admin || busy} className="space-y-3">
          <label className="flex gap-2 text-sm">
            <input
              type="checkbox"
              checked={data.enabled}
              onChange={(e) => change("enabled", e.target.checked)}
            />
            Enable AI drafting
          </label>
          <label className="flex gap-2 text-sm">
            <input
              type="checkbox"
              checked={data.share_conversations}
              onChange={(e) => change("share_conversations", e.target.checked)}
            />
            Allow customer conversations and selected product information to be
            sent to this verified provider
          </label>
          <Field label="Model">
            <input
              className={inputClass}
              value={data.model}
              onChange={(e) => change("model", e.target.value)}
            />
          </Field>
          <Field label="Default language">
            <Select
              value={data.language}
              onChange={(v) => change("language", v)}
              options={languages}
            />
          </Field>
          <Field label="Default tone">
            <input
              className={inputClass}
              value={data.tone}
              onChange={(e) => change("tone", e.target.value)}
            />
          </Field>
          <Field label="Approved business knowledge — fit/GSM, delivery, payment, returns and production terms">
            <textarea
              rows={8}
              className={inputClass}
              value={data.approved_knowledge}
              onChange={(e) => change("approved_knowledge", e.target.value)}
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ["daily_request_limit", "Daily request limit"],
              ["daily_usd_limit", "Daily reserved spend limit (USD)"],
              [
                "input_usd_per_million",
                "Verified input price / million tokens (USD)",
              ],
              [
                "output_usd_per_million",
                "Verified output price / million tokens (USD)",
              ],
            ].map(([k, label]) => (
              <Field key={k} label={label}>
                <input
                  type="number"
                  min="0"
                  step="any"
                  className={inputClass}
                  value={data[k]}
                  onChange={(e) => change(k, Number(e.target.value))}
                />
              </Field>
            ))}
          </div>
          <div className="flex gap-3">
            <button
              className={buttonClass}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.put("/growth/settings", data);
                  toast.success("Settings saved");
                  await load();
                } catch (e) {
                  toast.error(errorText(e));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save settings
            </button>
            <button
              className={buttonClass}
              onClick={async () => {
                setBusy(true);
                try {
                  const r = await api.post("/growth/connection-test");
                  toast.success(r.data.message);
                  await load();
                } catch (e) {
                  toast.error(errorText(e));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Test saved connection
            </button>
          </div>
        </fieldset>
        <p className="mt-3 text-xs text-slate-500">
          Only administrators can change settings. Connection tests use
          synthetic text and count toward limits. USD figures reserve a
          conservative maximum; they are not provider invoices. Failed requests
          retain reservations to avoid uncertain duplicate charges.
        </p>
      </Panel>
      <Panel>
        <h2 className="font-bold">Recent AI usage (UTC days)</h2>
        {usage.length ? (
          usage.map((u) => (
            <p className="border-b py-2 text-sm" key={u.day}>
              {u.day} • {u.requests} requests • {u.tokens} reported tokens • $
              {(u.reserved_micro_usd / 1000000).toFixed(4)} reserved
            </p>
          ))
        ) : (
          <p className="text-sm">No AI requests yet.</p>
        )}
      </Panel>
    </>
  );
}
